import { rank, route, type Finding } from '@/shared/ui/council'
import type {
  ImpactResponse, MoneyResponse, PortfolioOverviewResponse, ProgramCoverageResponse,
  QualityResponse, GapsResponse,
} from '@/shared/api/types'

export type { Finding }

export interface Board {
  overview: PortfolioOverviewResponse | null
  money: MoneyResponse | null
  impact: ImpactResponse | null
  coverage: ProgramCoverageResponse | null
  gaps: GapsResponse | null
  quality: QualityResponse | null
}

const n = (v: string | number | null | undefined): number | null => {
  if (v == null) return null
  const x = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(x) ? x : null
}

/**
 * The programmes council — a delivery portfolio read as decisions.
 *
 * ## The one thing a portfolio board must not do
 *
 * Report the SUM of programme beneficiaries as a headcount. If the same four thousand
 * households appear in a bursary, a water project and a boda scheme, the sum is three times
 * the number of people, and a press release quoting it is not wrong so much as answering a
 * question nobody asked. Every per-person figure here uses `distinctSubjects`, and where
 * de-duplication could not be performed the council says how many people that covers rather
 * than quietly counting them as unique.
 *
 * ## Three claims it refuses to make
 *
 * **It never calls an anomaly fraud.** A round-number pattern or an outlying mobilizer is a
 * reason to look, and the platform's own flags are named "for review" for that reason.
 *
 * **It never reports uplift as impact where there is no control.** Difference-in-differences
 * needs both arms; a treated-only mean is a before-and-after, and calling it an effect is the
 * most common way a programme claims credit for a season.
 *
 * **It never treats unknown eligibility as zero coverage.** A ward whose denominator is
 * missing is unmeasured, not unserved, and sending a team there on that basis is a decision
 * made on an absence.
 */
export function advise(b: Board): Finding[] {
  const out: Finding[] = []
  const num = (v: number | null | undefined) =>
    v == null ? '—' : Math.round(v).toLocaleString()
  const pct = (v: number | null | undefined) =>
    v == null ? '—' : `${Math.round(v * 100)}%`
  const o = b.overview

  // ── how many PEOPLE, not how many enrolments ──────────────────────────────
  if (o) {
    const inflation = o.distinctSubjects > 0 ? o.beneficiaryTotal / o.distinctSubjects : 1
    out.push({
      id: 'headcount',
      severity: o.unmatchableSubjects > o.distinctSubjects * 0.25 ? 'serious' : 'watch',
      headline: inflation > 1.2
        ? `${num(o.beneficiaryTotal)} enrolments across the portfolio are ${num(o.distinctSubjects)} actual people.`
        : `${num(o.distinctSubjects)} distinct people are enrolled across ${num(o.programmes)} programmes.`,
      evidence: `Sum of programme rows ${num(o.beneficiaryTotal)}; de-duplicated headcount `
        + `${num(o.distinctSubjects)}. ${num(o.unmatchableSubjects)} beneficiaries carry no national ID `
        + 'and could not be compared at all.',
      action: o.unmatchableSubjects > 0
        ? `Quote the de-duplicated figure, never the sum — the sum counts somebody in three programmes three times. And treat it as a ceiling: ${num(o.unmatchableSubjects)} people could not be matched, so the real duplicate count is at least what is shown and probably more.`
        : 'Quote the de-duplicated figure in anything public. The sum of programme rows counts a household in three programmes three times, and an opponent will find that.',
      votesAtStake: o.distinctSubjects,
    })

    const net = n(o.netCostPerUniquePerson)
    const per = n(o.costPerBeneficiary)
    if (net != null && per != null && net > per * 1.2) {
      out.push({
        id: 'cost',
        severity: 'watch',
        headline: `Each PERSON reached has cost ${o.currency ?? 'KES'} ${num(net)}, not ${num(per)}.`,
        evidence: `${o.currency ?? 'KES'} ${num(n(o.spendTotal))} spent`
          + (o.budgetTotal ? ` of ${num(n(o.budgetTotal))} budgeted` : '')
          + `. The lower figure divides by enrolments; the higher divides by people.`,
        action: 'Budget against the higher number. Planning on cost-per-enrolment quietly assumes every enrolment is a new person, and in a portfolio with overlap it never is.',
        votesAtStake: 0,
      })
    }

    if (o.currencyMixedReason) {
      out.push({
        id: 'currency',
        severity: 'serious',
        headline: 'Money figures on this board mix currencies.',
        evidence: o.currencyMixedReason,
        action: 'Do not add these totals together or quote them as one figure. Split the portfolio by currency, or convert with a stated rate and date — an unlabelled mixed total is wrong in a way nobody can see.',
        votesAtStake: 0,
      })
    }
  }

  // ── the money, and whether it reconciles ──────────────────────────────────
  if (b.money) {
    const m = b.money
    if (!m.withinTolerance) {
      const gap = n(m.discrepancy)
      out.push({
        id: 'reconciliation',
        severity: 'critical',
        headline: `The ledger and the recorded spend do not agree${gap != null ? ` — a gap of ${m.currency ?? 'KES'} ${num(Math.abs(gap))}.` : '.'}`,
        evidence: `Spend recorded ${num(n(m.spendTotal))}, ledger sum ${num(n(m.ledgerSum))}`
          + (m.reconciledAt ? `, last reconciled ${new Date(m.reconciledAt).toLocaleDateString()}.` : '.')
          + (n(m.spendUnattributed) ? ` ${num(n(m.spendUnattributed))} of spend is not attributed to any beneficiary.` : ''),
        action: 'Resolve this before any cost figure on this board is used externally. An unreconciled gap is the single easiest thing for an auditor or an opponent to find, and every per-person cost above is computed on the disputed number.',
        votesAtStake: 0,
      })
    }

    if (m.runwayDays != null && m.runwayDays < 90) {
      out.push({
        id: 'runway',
        severity: m.runwayDays < 30 ? 'critical' : 'serious',
        headline: `At the current burn the budget runs out in ${m.runwayDays} days.`,
        evidence: `Burning ${m.currency ?? 'KES'} ${num(n(m.burnRatePerDay))} a day`
          + (m.projectedExhaustion ? `; projected exhaustion ${new Date(m.projectedExhaustion).toLocaleDateString()}.` : '.'),
        action: 'Decide now which programmes stop and which continue. A portfolio that runs out mid-delivery leaves half-finished commitments to named people, which costs more politically than never having started them.',
        votesAtStake: 0,
      })
    }
  }

  // ── did any of it work? ───────────────────────────────────────────────────
  if (b.impact) {
    const i = b.impact
    const did = n(i.differenceInDifferences)
    const treated = n(i.meanUpliftTreated)
    if (i.controlSize === 0 || i.controlMeasured === 0) {
      out.push({
        id: 'no-control',
        severity: 'serious',
        headline: 'There is no control group, so nothing here measures impact.',
        evidence: `${num(i.measured)} of ${num(i.measurable)} measurable beneficiaries re-assessed`
          + (treated != null ? `, mean change ${treated.toFixed(2)}.` : '.')
          + ` ${num(i.controlSize)} people in a control arm.`,
        action: 'A before-and-after on treated people alone cannot separate the programme from the season, the campaign or anything else that moved at the same time. Draw a control before the next round — retrofitting one afterwards is not possible.',
        votesAtStake: i.measured,
      })
    } else if (did != null) {
      out.push({
        id: 'impact',
        severity: did > 0 ? 'good' : 'serious',
        headline: did > 0
          ? `Programmes are moving support by ${did.toFixed(2)} against a control.`
          : `Against a control, programmes are not moving support (${did.toFixed(2)}).`,
        evidence: `Treated mean ${treated?.toFixed(2) ?? '—'} over ${num(i.measured)} people;`
          + ` control mean ${n(i.meanUpliftControl)?.toFixed(2) ?? '—'} over ${num(i.controlMeasured)}.`,
        action: did > 0
          ? 'This is the figure to fund against — it is the difference the programme made, not the change that happened. Check which categories carry it before scaling everything equally.'
          : 'Before cutting, check that the control is comparable and that measurement happened late enough for an effect to appear. A null result from a badly-drawn control has closed good programmes before.',
        votesAtStake: i.measured,
      })
    }

    const unmeasured = i.excludedUnlinked + i.excludedNoBaseline + i.excludedNotEnrolled
    if (unmeasured > i.measured) {
      out.push({
        id: 'measurement',
        severity: 'watch',
        headline: `More beneficiaries are excluded from measurement than included in it.`,
        evidence: `${num(i.excludedUnlinked)} not linked to a person, ${num(i.excludedNoBaseline)} with`
          + ` no baseline, ${num(i.excludedNotEnrolled)} never enrolled — against ${num(i.measured)} measured.`,
        action: 'Whatever the impact figure says, it describes a minority of the people served. Fix the baseline capture at enrolment; it costs nothing at the door and cannot be recovered later.',
        votesAtStake: unmeasured,
      })
    }
  }

  // ── where nobody was reached ──────────────────────────────────────────────
  if (b.gaps && b.gaps.gaps.length > 0) {
    const g = b.gaps
    out.push({
      id: 'gaps',
      severity: g.gaps.length > 10 ? 'serious' : 'watch',
      headline: `${num(g.totalAvailable)} areas have eligible people and nobody enrolled.`,
      evidence: g.gaps.slice(0, 4)
        .map((r) => `${r.unitName}`).join(' · ')
        + (g.unknownEligibility.length > 0
          ? `. A further ${g.unknownEligibility.length} areas have no eligibility figure at all.` : ''),
      action: 'These are the cheapest places to add reach, because the denominator is known and the numerator is zero. Areas with unknown eligibility are NOT on this list and must not be treated as served — they are unmeasured, which is a different problem needing a different fix.',
      votesAtStake: g.totalAvailable,
      cellCodes: g.gaps.slice(0, 6).map((r) => r.unitName),
    })
  }

  if (b.coverage) {
    const c = b.coverage
    const weighted = n(c.weightedMean)
    const unweighted = n(c.unweightedMean)
    if (weighted != null && unweighted != null && Math.abs(weighted - unweighted) > 0.05) {
      out.push({
        id: 'coverage-shape',
        severity: 'watch',
        headline: weighted < unweighted
          ? 'Coverage looks better per area than it does per person.'
          : 'Coverage looks better per person than it does per area.',
        evidence: `Weighted by population ${pct(weighted)}, unweighted across areas ${pct(unweighted)}.`
          + (c.unknownEligibilityCount > 0
            ? ` ${num(c.unknownEligibilityCount)} areas have no eligibility figure.` : ''),
        action: weighted < unweighted
          ? 'The programme is reaching many small areas and missing the big ones. If the objective is votes rather than breadth, that is backwards — a ward of forty thousand and a ward of two hundred are not one unit each.'
          : 'The programme is concentrated in populous areas. That is efficient for reach and leaves small areas untouched, which is a defensible choice but should be a deliberate one.',
        votesAtStake: 0,
      })
    }
  }

  // ── is the record good enough to say any of this? ─────────────────────────
  if (b.quality) {
    const q = b.quality.completeness
    const worst = Math.max(q.unlinked, q.missingNationalId, q.missingPostEnrolmentReading)
    if (q.beneficiaries > 0 && worst / q.beneficiaries > 0.2) {
      out.push({
        id: 'completeness',
        severity: worst / q.beneficiaries > 0.4 ? 'serious' : 'watch',
        headline: 'A large share of beneficiary records is incomplete.',
        evidence: `${num(q.unlinked)} not linked to a person, ${num(q.missingNationalId)} with no`
          + ` national ID, ${num(q.missingPostEnrolmentReading)} with no post-enrolment reading,`
          + ` out of ${num(q.beneficiaries)}.`,
        action: 'Every de-duplicated and per-person figure on this board depends on these fields. Until they are filled, treat the headcount as a ceiling and the impact figures as describing whoever happened to be complete.',
        votesAtStake: worst,
      })
    }
    if (q.disbursementsWithoutBeneficiary > 0) {
      out.push({
        id: 'orphan-spend',
        severity: 'serious',
        headline: `${num(q.disbursementsWithoutBeneficiary)} disbursements are not attached to a beneficiary.`,
        evidence: `${num(q.milestonesWithoutOwner)} milestones also have no owner.`,
        action: 'Money that left without a named recipient is the finding an auditor opens with. Attach them or record why they cannot be attached — an explanation on file is a different thing from a blank.',
        votesAtStake: 0,
      })
    }
    const anomalies = b.quality.anomalies as unknown as Record<string, unknown>
    const flagged = typeof anomalies?.flaggedCount === 'number' ? anomalies.flaggedCount : null
    if (flagged != null && flagged > 0) {
      out.push({
        id: 'anomalies',
        severity: 'watch',
        headline: `${num(flagged)} records are flagged for review.`,
        evidence: 'Raised by the nightly rules on the Delivery quality screen.',
        action: 'A flag is a reason to look, not an accusation, and it must not be described as fraud to anybody. Work them in order and record the outcome — an unreviewed flag is worse than no flag, because it looks like something was known and ignored.',
        votesAtStake: flagged,
      })
    }
  }

  if (out.length === 0) {
    out.push({
      id: 'quiet',
      severity: 'good',
      headline: 'Nothing in the portfolio crosses a threshold.',
      evidence: o ? `${num(o.programmes)} programmes, ${num(o.distinctSubjects)} distinct people.` : '',
      action: 'No action. Widen the window if you are checking whether delivery has been slipping.',
      votesAtStake: 0,
    })
  }

  return rank(out)
}

const ROUTES: { id: string; words: string[] }[] = [
  { id: 'headcount', words: ['how many', 'people', 'reach', 'beneficiar', 'headcount', 'dedup', 'duplicate', 'quote'] },
  { id: 'cost', words: ['cost', 'per person', 'value', 'efficien', 'worth'] },
  { id: 'reconciliation', words: ['ledger', 'reconcil', 'audit', 'accounts', 'discrepan', 'missing money'] },
  { id: 'runway', words: ['runway', 'budget', 'run out', 'burn', 'afford', 'money left'] },
  { id: 'impact', words: ['impact', 'work', 'effect', 'uplift', 'difference', 'did it'] },
  { id: 'no-control', words: ['control', 'baseline', 'prove', 'attribut', 'causal'] },
  { id: 'measurement', words: ['measure', 'excluded', 'unmeasured', 'coverage of measurement'] },
  { id: 'gaps', words: ['gap', 'nobody', 'missing area', 'where should', 'expand', 'untouched', 'unserved'] },
  { id: 'coverage-shape', words: ['coverage', 'spread', 'weighted', 'small ward', 'concentrat'] },
  { id: 'completeness', words: ['data quality', 'incomplete', 'missing', 'record', 'clean'] },
  { id: 'orphan-spend', words: ['disburse', 'unattached', 'orphan', 'without beneficiary'] },
  { id: 'anomalies', words: ['anomal', 'flag', 'fraud', 'suspicious', 'review'] },
  { id: 'currency', words: ['currency', 'exchange', 'mixed'] },
  { id: 'quiet', words: ['quiet', 'anything', 'all clear', 'ok', 'fine'] },
]

export function ask(question: string, findings: Finding[]) {
  return route(question, findings, ROUTES)
}

/**
 * The portfolio, written out for the model.
 *
 * The three refusals are stated as instructions because a model given these counts breaks
 * all three: it will add the beneficiary sum as a headcount, call a treated-only mean an
 * impact, and describe a flag as fraud.
 */
export function programContext(b: Board, findings: Finding[]): string {
  const num = (v: number | string | null | undefined) => {
    const x = n(typeof v === 'string' ? v : v ?? null)
    return x == null ? '—' : Math.round(x).toLocaleString()
  }
  const o = b.overview
  const m = b.money
  const i = b.impact

  return [
    "You are advising a Kenyan campaign on its delivery programmes — bursaries, water",
    'projects, boda schemes and the like. Answer in plain language a campaign manager can act',
    'on. Three rules you must not break:',
    '(1) NEVER report the sum of programme beneficiaries as a headcount. The same household',
    'appears in several programmes; use the de-duplicated distinct-people figure, and note that',
    'people with no national ID could not be de-duplicated at all.',
    '(2) A change measured only on treated people is a before-and-after, NOT an impact. Only',
    'call something an effect where there is a control arm and a difference-in-differences.',
    '(3) A flagged record is a reason to look. Never describe it as fraud or accuse anybody.',
    'Also: an area with UNKNOWN eligibility is unmeasured, not unserved — do not send teams',
    'there on the basis of a missing denominator.',
    '',
    o ? `PORTFOLIO: ${num(o.programmes)} programmes. Enrolment rows sum to `
      + `${num(o.beneficiaryTotal)}, which is ${num(o.distinctSubjects)} DISTINCT PEOPLE. `
      + `${num(o.unmatchableSubjects)} beneficiaries have no national ID and could not be matched. `
      + `Status: ${Object.entries(o.byStatus ?? {}).map(([k, v]) => `${k} ${v}`).join(', ') || 'none'}. `
      + `Categories: ${Object.entries(o.byCategory ?? {}).map(([k, v]) => `${k} ${v}`).join(', ') || 'none'}.`
      : 'PORTFOLIO: no overview available.',
    o ? `MONEY: budget ${num(o.budgetTotal)}, spend ${num(o.spendTotal)}, unattributed `
      + `${num(o.spendUnattributed)}, cost per enrolment ${num(o.costPerBeneficiary)}, cost per `
      + `DISTINCT PERSON ${num(o.netCostPerUniquePerson)} (${o.currency ?? 'KES'})`
      + (o.currencyMixedReason ? ` — WARNING, currencies are mixed: ${o.currencyMixedReason}` : '.')
      : '',
    m ? `LEDGER: ledger sum ${num(m.ledgerSum)}, discrepancy ${num(m.discrepancy)}, within tolerance: `
      + `${m.withinTolerance}. Burn ${num(m.burnRatePerDay)}/day, runway `
      + `${m.runwayDays ?? 'unknown'} days.` : '',
    i ? `IMPACT: ${num(i.programmesMeasured)} of ${num(i.programmes)} programmes measured. `
      + `${num(i.measured)} of ${num(i.measurable)} measurable beneficiaries re-assessed. `
      + `Treated mean uplift ${i.meanUpliftTreated ?? 'n/a'}; control arm ${num(i.controlSize)} people, `
      + `${num(i.controlMeasured)} measured, mean ${i.meanUpliftControl ?? 'n/a'}; `
      + `difference-in-differences ${i.differenceInDifferences ?? 'NOT COMPUTABLE'}. `
      + `Excluded: ${num(i.excludedUnlinked)} unlinked, ${num(i.excludedNoBaseline)} no baseline, `
      + `${num(i.excludedNotEnrolled)} not enrolled.` : '',
    b.coverage ? `COVERAGE: weighted by population ${b.coverage.weightedMean ?? 'n/a'}, unweighted `
      + `${b.coverage.unweightedMean ?? 'n/a'}, ${num(b.coverage.unknownEligibilityCount)} areas with `
      + 'no eligibility figure.' : '',
    b.gaps ? `GAPS: ${num(b.gaps.totalAvailable)} areas have eligible people and zero enrolments — `
      + `${b.gaps.gaps.slice(0, 8).map((r) => r.unitName).join(', ') || 'none listed'}. `
      + `${b.gaps.unknownEligibility.length} areas have unknown eligibility.` : '',
    b.quality ? `RECORD QUALITY: of ${num(b.quality.completeness.beneficiaries)} beneficiaries, `
      + `${num(b.quality.completeness.unlinked)} unlinked, `
      + `${num(b.quality.completeness.missingNationalId)} without a national ID, `
      + `${num(b.quality.completeness.missingPostEnrolmentReading)} without a post-enrolment reading, `
      + `${num(b.quality.completeness.disbursementsWithoutBeneficiary)} disbursements unattached.` : '',
    '',
    'WHAT OUR OWN RULES ALREADY FLAGGED (agree, refine or disagree):',
    ...findings.map((f) => `- [${f.severity}] ${f.headline} ${f.evidence}`),
  ].filter(Boolean).join('\n')
}
